import { PhraseCategory, Phrases } from './types';

export const WIZARD_WIDTH_PX = 40;
export const MOBILE_MAX_WIDTH_PX = 767;
export const LABEL_ANCHOR_OFFSET_PX = 24;
export const MIN_RENDER_WIDTH_PX = 40;
const PHRASE_SELECTION_STATE_STORAGE_KEY = 'quoteMagicPhraseSelectionState';

type PhraseSelectionState = Record<PhraseCategory, string[]>;

type LabelPosition = 'left' | 'right';

type LabelLayoutParams = {
  trackLeftPx: number;
  wizardPositionPx: number;
  labelWidthPx: number;
  viewportWidthPx: number;
  isMobile: boolean;
};

export type LabelLayout = {
  position: LabelPosition;
  shouldTruncate: boolean;
  maxLabelWidthPx?: number;
  renderWidthPx: number;
};

const STEP_PHRASE_CATEGORIES: Record<string, PhraseCategory> = {
  quote_magic_coverage_level: 'beginning',
  quote_magic_start: 'middle',
  address: 'end',
};

export const STEPS_WITHOUT_PROGRESS_BAR = new Set([
  'quote_magic_load',
  'quote_magic_panel',
]);

const createEmptyPhraseSelectionState = (): PhraseSelectionState => ({
  beginning: [],
  middle: [],
  end: [],
});

const sanitizeCategorySelection = (
  category: PhraseCategory,
  value: unknown
): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const validPhrases = new Set(Phrases[category]);
  const uniquePhrases = new Set<string>();

  value.forEach((phrase) => {
    if (typeof phrase === 'string' && validPhrases.has(phrase)) {
      uniquePhrases.add(phrase);
    }
  });

  return Array.from(uniquePhrases);
};

const normalizePhraseSelectionState = (
  value: unknown
): PhraseSelectionState => {
  if (!value || typeof value !== 'object') {
    return createEmptyPhraseSelectionState();
  }

  const draftState = value as Partial<Record<PhraseCategory, unknown>>;

  return {
    beginning: sanitizeCategorySelection('beginning', draftState.beginning),
    middle: sanitizeCategorySelection('middle', draftState.middle),
    end: sanitizeCategorySelection('end', draftState.end),
  };
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  return window.sessionStorage;
};

const readPhraseSelectionState = (): PhraseSelectionState => {
  const sessionStorageRef = getSessionStorage();

  if (!sessionStorageRef) {
    return createEmptyPhraseSelectionState();
  }

  try {
    const persistedState = sessionStorageRef.getItem(
      PHRASE_SELECTION_STATE_STORAGE_KEY
    );

    if (!persistedState) {
      return createEmptyPhraseSelectionState();
    }

    return normalizePhraseSelectionState(JSON.parse(persistedState));
  } catch (_error) {
    return createEmptyPhraseSelectionState();
  }
};

const persistPhraseSelectionState = (state: PhraseSelectionState): void => {
  const sessionStorageRef = getSessionStorage();

  if (!sessionStorageRef) {
    return;
  }

  try {
    sessionStorageRef.setItem(
      PHRASE_SELECTION_STATE_STORAGE_KEY,
      JSON.stringify(state)
    );
  } catch (_error) {
    // Ignore storage write failures and continue with in-memory behavior.
  }
};

const clearPhraseSelectionState = (): void => {
  const sessionStorageRef = getSessionStorage();

  if (!sessionStorageRef) {
    return;
  }

  try {
    sessionStorageRef.removeItem(PHRASE_SELECTION_STATE_STORAGE_KEY);
  } catch (_error) {
    // Ignore storage cleanup failures.
  }
};

export const getPhraseCategoryForStep = (
  stepName: string
): PhraseCategory | null => {
  return STEP_PHRASE_CATEGORIES[stepName] ?? null;
};

export const getPhraseForStep = (stepName: string): string | null => {
  const category = getPhraseCategoryForStep(stepName);

  if (!category) {
    return null;
  }

  const phraseSelectionState = readPhraseSelectionState();
  const usedPhrases = new Set(phraseSelectionState[category]);
  let availablePhrases = Phrases[category].filter(
    (phrase) => !usedPhrases.has(phrase)
  );

  if (availablePhrases.length === 0) {
    phraseSelectionState[category] = [];
    availablePhrases = [...Phrases[category]];
  }

  const randomIndex = Math.floor(Math.random() * availablePhrases.length);
  const selectedPhrase = availablePhrases[randomIndex];

  phraseSelectionState[category] = [
    ...phraseSelectionState[category],
    selectedPhrase,
  ];
  persistPhraseSelectionState(phraseSelectionState);

  if (category === 'end') {
    clearPhraseSelectionState();
  }

  return selectedPhrase;
};

export const __resetPhraseSelectionStateForTests = (): void => {
  clearPhraseSelectionState();
};

/**
 * Calculates progress label position and truncation behavior based on available viewport space.
 */
export const calculateLabelLayout = ({
  trackLeftPx,
  wizardPositionPx,
  labelWidthPx,
  viewportWidthPx,
  isMobile,
}: LabelLayoutParams): LabelLayout => {
  const wizardViewportPosition = trackLeftPx + wizardPositionPx;
  const availableWidthOnRight =
    viewportWidthPx - (wizardViewportPosition + LABEL_ANCHOR_OFFSET_PX);

  const position: LabelPosition =
    labelWidthPx > availableWidthOnRight ? 'left' : 'right';

  const availableWidthOnChosenSide =
    position === 'right'
      ? availableWidthOnRight
      : wizardViewportPosition - LABEL_ANCHOR_OFFSET_PX;

  const normalizedAvailableWidth = Math.max(
    0,
    Math.floor(availableWidthOnChosenSide)
  );
  const shouldTruncate = isMobile && labelWidthPx > normalizedAvailableWidth;
  const truncatedRenderWidthPx = Math.max(
    normalizedAvailableWidth,
    MIN_RENDER_WIDTH_PX
  );
  const renderWidthPx = shouldTruncate ? truncatedRenderWidthPx : labelWidthPx;

  return {
    position,
    shouldTruncate,
    maxLabelWidthPx: shouldTruncate ? truncatedRenderWidthPx : undefined,
    renderWidthPx,
  };
};
